/**
 * Archived projects (vfs-service src/routes/projects.routes.js, through the sandbox gateway under the vfs path the web
 * app already uses, with the person's authorized fetch). An archived project is hidden from the lists for everyone it is
 * shared with; only its owner archives or unarchives it, and nothing of it is deleted.
 * - `POST /api/v1/projects/:id/archive` | `/unarchive` -> `{ projectId, archived, archivedAt, archivedBy }` (403
 *   `owner_only` for anyone else);
 * - `GET /api/v1/projects?owner=<sub>&archived=only` -> `{ projects }`: the archived projects the person owns or that are
 *   shared with them (`role` other than owner), each with `archivedAt`, `archivedBy` and `owner`;
 * - `GET /api/v1/projects?archived=only` -> `{ projectIds }`: their ids, which the BFF's project list (built from the
 *   person's own registry too) is filtered by (MiaPlatformDataService.getProjects, `withoutArchived`).
 * Every change is announced with ARCHIVE_CHANGED so the open lists and the project's Home read it again.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');
const seg = (value) => encodeURIComponent(String(value));

export const ARCHIVE_CHANGED = 'flow:projectArchiveChanged';

async function failure(response, fallback) {
  const data = await response.json().catch(() => null);
  const error = new Error(data?.error || `${fallback} (${response.status})`);
  error.status = response.status;
  if (data?.code) { error.code = data.code; }
  return error;
}

// The ids, read again at most every few seconds (every list of the app asks for them), and at once after a change.
const IDS_TTL_MS = 10000;
let cached = null; // { at, ids: Promise<Set<string>> }

/** Forgets the cached ids and tells every open view that `projectId` was archived or unarchived. */
export function announceArchiveChanged(projectId, archived) {
  cached = null;
  if (typeof window !== 'undefined') { window.dispatchEvent(new CustomEvent(ARCHIVE_CHANGED, { detail: { projectId, archived } })); }
}

async function setArchived(projectId, archive) {
  const response = await getAuthorizedFetch()(`${base()}/api/v1/projects/${seg(projectId)}/${archive ? 'archive' : 'unarchive'}`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) { throw await failure(response, archive ? 'The project could not be archived' : 'The project could not be unarchived'); }
  const answer = await response.json();
  announceArchiveChanged(projectId, Boolean(answer?.archived));
  return answer;
}

export const archiveProject = (projectId) => setArchived(projectId, true);
export const unarchiveProject = (projectId) => setArchived(projectId, false);

/** The ids of the archived projects the person sees; an empty set where vfs is not configured or does not answer. */
export function archivedProjectIds() {
  if (!base()) { return Promise.resolve(new Set()); }
  if (cached && Date.now() - cached.at < IDS_TTL_MS) { return cached.ids; }
  const ids = (async() => {
    try {
      const response = await getAuthorizedFetch()(`${base()}/api/v1/projects?archived=only`, { headers: { Accept: 'application/json' } });
      if (!response.ok) { return new Set(); }
      const body = await response.json();
      return new Set(Array.isArray(body?.projectIds) ? body.projectIds : []);
    } catch {
      return new Set();
    }
  })();
  cached = { at: Date.now(), ids };
  return ids;
}

/** A project list's items without the archived ones (by `_id`, `id` or `projectId`); unchanged when none are known. */
export async function withoutArchived(items) {
  if (!Array.isArray(items) || items.length === 0) { return items; }
  const ids = await archivedProjectIds();
  if (ids.size === 0) { return items; }
  return items.filter((item) => !ids.has(item?._id || item?.id || item?.projectId));
}

/**
 * The archived projects the person sees, as the Projects list shows a project: `{ _id, name, description, updatedAt,
 * createdWith, archivedAt, archivedBy, owner, isOwner, sharedRole, sharedBy, creatorName, creatorEmail, creatorId }`.
 */
export async function listArchivedProjects(sub) {
  if (!base() || !sub) { return []; }
  const response = await getAuthorizedFetch()(`${base()}/api/v1/projects?owner=${seg(sub)}&archived=only`, { headers: { Accept: 'application/json' } });
  if (!response.ok) { throw await failure(response, 'The archived projects could not be listed'); }
  const { projects = [] } = await response.json();
  return projects.map((project) => {
    const isOwner = !project.role || project.role === 'owner';
    const owner = project.owner || {};
    return {
      _id: project.projectId,
      name: project.name || project.projectId,
      description: project.description || '',
      updatedAt: project.updatedAt || null,
      createdAt: project.createdAt || null,
      createdWith: project.createdWith || null,
      archived: true,
      archivedAt: project.archivedAt || null,
      archivedBy: project.archivedBy || null,
      isOwner,
      ...(isOwner ? {} : { sharedRole: project.role, sharedBy: owner.name || owner.email || null }),
      creatorName: owner.name || owner.email || null,
      creatorEmail: owner.email || null,
      creatorId: owner.sub || null,
    };
  });
}
